import React from "react";
import {
  Paper,
  Grid,
  IconButton,
  Typography,
  Box,
  Link as MuiLink,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { ZincProduct } from "@t3/shared";
import { useCartContext } from "@/context/CartContext";
import ProductImage from "@/components/productImage";
import convertToPoints from "@/utils/convertToPoints";

interface CartZincItemProps {
  product: ZincProduct;
  index: number;
}

export const CartZincItem: React.FC<CartZincItemProps> = ({
  product,
  index,
}) => {
  const { removeZincItem, cartUpdateLoading } = useCartContext();

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 2,
        p: 2.5,
        borderRadius: 2,
        transition: "box-shadow 0.2s ease",
      }}
      data-cypress={`checkout-zinc-item`}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Product Image */}
        <Grid>
          <ProductImage card={product as any} height="100px" width="100px" />
        </Grid>

        {/* Product Details */}
        <Grid flex={1} sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: 16,
              fontWeight: 600,
              mb: 0.5,
              lineHeight: 1.3,
            }}
            data-cypress="checkout-zinc-item-title"
          >
            {product.title || "Product"}
          </Typography>
          <MuiLink
            component={Link}
            to={`/shop/products/${product.utid}`}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              textDecoration: "none",
              color: "primary.main",
              display: "inline-block",
              mb: 1,
            }}
            data-cypress="view-product-page-link"
          >
            View Product Page
          </MuiLink>

          <Box sx={{ mt: 1.5, display: { xs: "none", sm: "block" } }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: 18,
              }}
              data-cypress="checkout-zinc-item-points"
            >
              {Math.round(convertToPoints(product.amount) * product.quantity)}{" "}
              Points
            </Typography>
            {product.quantity > 1 && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  mt: 0.25,
                }}
                data-cypress="checkout-zinc-item-quantity-breakdown"
              >
                ({convertToPoints(product.amount)} pts × {product.quantity})
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Delete Button */}
        <Grid sx={{ display: { xs: "none", sm: "block" } }}>
          <IconButton
            aria-label="delete"
            size="medium"
            data-cypress="remove-item-button"
            onClick={() => removeZincItem(product.utid, index)}
            disabled={cartUpdateLoading}
            sx={{
              color: "error.main",
            }}
          >
            <DeleteIcon fontSize="medium" />
          </IconButton>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          alignItems: "center",
          justifyContent: "space-between",
          mt: 0.5,
        }}
      >
        <Typography
          sx={{
            color: "red",
            cursor: "pointer",
          }}
          onClick={() => removeZincItem(product.utid, index)}
          data-cypress="remove-item-button"
          component="span"
          fontSize={14}
        >
          REMOVE
        </Typography>

        <Box sx={{ textAlign: "right", display: "flex" }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "primary.main",
              fontSize: 18,
            }}
            data-cypress="checkout-zinc-item-points"
          >
            {convertToPoints(product.amount) * product.quantity} Points
          </Typography>
          {product.quantity > 1 && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mt: 0.5,
                ml: 1,
              }}
              data-cypress="checkout-zinc-item-quantity-breakdown"
            >
              ({convertToPoints(product.amount)} pts × {product.quantity})
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
